import React, { useState, useEffect } from 'react';
import { Table, Tag, Modal, Spin, message, Button, Space, Segmented } from 'antd';
import { clientApi } from '../services/api';

const SYNC_TYPE_NAME = {
  positions: '资金持仓',
  orders: '当日委托',
  history_orders: '历史委托',
  history_deals: '历史成交',
};

export default function SyncLogPage() {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [details, setDetails] = useState([]);
  const [detailFilter, setDetailFilter] = useState('all');

  // 明细筛选：全部 / 失败(整体失败或资金持仓任一未同步) / 资金未同步 / 持仓未同步
  const isPositionSync = current && current.sync_type === 'positions';
  const filteredDetails = details.filter((d) => {
    if (detailFilter === 'failed') {
      if (d.status === 'failed') return true;
      if (isPositionSync && (!d.account_synced || !d.position_synced)) return true;
      return false;
    }
    if (detailFilter === 'account_fail') return isPositionSync && !d.account_synced;
    if (detailFilter === 'position_fail') return isPositionSync && !d.position_synced;
    return true;
  });

  const loadRecords = async () => {
    setLoading(true);
    try {
      const res = await clientApi.getSyncBatchRecords();
      if (res.success) setRecords(res.data || []);
      else message.error(res.message || '加载失败');
    } catch {
      message.error('加载同步日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecords(); }, []);

  const openDetail = async (rec) => {
    setCurrent(rec);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetails([]);
    try {
      const res = await clientApi.getSyncBatchDetails(rec.batch_no);
      if (res.success) setDetails(res.data || []);
      else message.error(res.message || '加载明细失败');
    } catch {
      message.error('加载同步明细失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const recordColumns = [
    { title: '批次号', dataIndex: 'batch_no', key: 'batch_no', width: 200,
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
    { title: '同步类型', dataIndex: 'sync_type', key: 'sync_type', width: 110,
      render: (v) => SYNC_TYPE_NAME[v] || v },
    { title: '操作人', dataIndex: 'operator', key: 'operator', width: 120,
      render: (v) => v || '-' },
    { title: '成功', dataIndex: 'success_count', key: 'success_count', width: 70,
      render: (v) => <Tag color="success">{v}</Tag> },
    { title: '失败', dataIndex: 'fail_count', key: 'fail_count', width: 70,
      render: (v) => <Tag color="error">{v}</Tag> },
    { title: '总数', dataIndex: 'total_count', key: 'total_count', width: 70 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => v === 'done' ? <Tag color="success">完成</Tag> : <Tag color="processing">进行中</Tag> },
    { title: '开始时间', dataIndex: 'started_at', key: 'started_at', width: 180,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '-' },
    { title: '操作', key: 'action', width: 100,
      render: (_, rec) => <Button type="link" onClick={() => openDetail(rec)}>查看明细</Button> },
  ];

  const detailColumns = [
    { title: '客户端', dataIndex: 'client_name', key: 'client_name', width: 180,
      render: (v, r) => <span><span style={{ color: '#1677ff' }}>{v || r.client_id}</span></span> },
    ...(isPositionSync ? [
      { title: '资金状态', dataIndex: 'account_synced', key: 'account_synced', width: 100,
        render: (v) => <Tag color={v ? 'success' : 'error'}>{v ? '已同步' : '未同步'}</Tag> },
      { title: '持仓状态', dataIndex: 'position_synced', key: 'position_synced', width: 100,
        render: (v) => <Tag color={v ? 'success' : 'error'}>{v ? '已同步' : '未同步'}</Tag> },
    ] : []),
    { title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => v === 'success' ? <Tag color="success">成功</Tag>
        : v === 'failed' ? <Tag color="error">失败</Tag>
          : v === 'running' ? <Tag color="processing">同步中</Tag>
            : <Tag color="default">等待中</Tag> },
    { title: '信息', dataIndex: 'message', key: 'message',
      render: (v) => <span style={{ color: '#999', fontSize: 12 }}>{v || '-'}</span> },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>同步日志</h2>
        <Space>
          <Button onClick={loadRecords}>刷新</Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={recordColumns}
        dataSource={records}
        pagination={{ pageSize: 10, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
        size="middle"
      />
      <Modal
        title={`同步明细${current ? `（${SYNC_TYPE_NAME[current.sync_type] || current.sync_type}）` : ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={720}
      >
        {current && (
          <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            批次号：<span style={{ fontFamily: 'monospace' }}>{current.batch_no}</span>
            ，开始时间：{current.started_at ? new Date(current.started_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '-'}
          </div>
        )}
        <Segmented
          style={{ marginBottom: 12 }}
          value={detailFilter}
          onChange={setDetailFilter}
          options={[
            { label: `全部 (${details.length})`, value: 'all' },
            { label: `失败 (${details.filter(d => d.status === 'failed' || (isPositionSync && (!d.account_synced || !d.position_synced))).length})`, value: 'failed' },
            ...(isPositionSync ? [
              { label: `资金未同步 (${details.filter(d => !d.account_synced).length})`, value: 'account_fail' },
              { label: `持仓未同步 (${details.filter(d => !d.position_synced).length})`, value: 'position_fail' },
            ] : []),
          ]}
        />
        <Spin spinning={detailLoading}>
          <Table
            rowKey="id"
            columns={detailColumns}
            dataSource={filteredDetails}
            pagination={{ pageSize: 10, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
            size="small"
            locale={{ emptyText: '暂无明细' }}
          />
        </Spin>
      </Modal>
    </div>
  );
}
